/*
 * Swan Jewel — filter panel behaviour and grid paging.
 *
 * 1. Group headers
 *    Dawn's <menu-drawer> binds a click handler to every <summary> inside
 *    itself and treats it as a slide-over submenu. Our filter groups are plain
 *    inline <details> that should expand in place, so we stop those clicks from
 *    reaching menu-drawer's handler. The native <details> toggle still happens:
 *    stopPropagation does not cancel the default action.
 *
 * 2. Price slider
 *    The two range inputs have no name attribute, so they never reach the form
 *    data. Dragging writes the value into Dawn's From / To fields, and the
 *    input event bubbling up to the form is what triggers Dawn's debounced
 *    filter request.
 *
 * 3. Endless scroll
 *    Shopify caps a collection page at 36 products, so longer collections still
 *    paginate. The pagination links are hidden and the next page is fetched
 *    automatically when a sentinel below the grid comes into view, appending
 *    its cards. If the browser has no IntersectionObserver, or a fetch fails,
 *    the original pagination is put back so nobody is ever stranded.
 *
 * All listeners are delegated from the document, or re-attached by a mutation
 * observer, so they survive the grid being re-rendered after each filter change.
 */
(function () {
  var GROUP_SUMMARY = '.swan-facets__summary';

  function isGroupSummary(target) {
    return target && target.closest && target.closest(GROUP_SUMMARY);
  }

  document.addEventListener(
    'click',
    function (event) {
      if (isGroupSummary(event.target)) event.stopPropagation();
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      if (isGroupSummary(event.target)) event.stopPropagation();
    },
    true
  );

  /* ---------------------------------------------------------------- price */

  function paint(wrap, low, high) {
    var minInput = wrap.querySelector('[data-swan-range-min]');
    var floor = parseFloat(minInput.min);
    var ceiling = parseFloat(minInput.max);
    var span = ceiling - floor;
    if (!span) return;
    wrap.style.setProperty('--swan-range-min-pct', ((low - floor) / span) * 100 + '%');
    wrap.style.setProperty('--swan-range-max-pct', ((high - floor) / span) * 100 + '%');
  }

  function onRangeInput(event) {
    var input = event.target;
    if (!input.classList || !input.classList.contains('swan-range__input')) return;

    var wrap = input.closest('[data-swan-range]');
    if (!wrap) return;

    var minInput = wrap.querySelector('[data-swan-range-min]');
    var maxInput = wrap.querySelector('[data-swan-range-max]');
    var low = parseFloat(minInput.value);
    var high = parseFloat(maxInput.value);

    if (low > high) {
      if (input === minInput) {
        low = high;
        minInput.value = high;
      } else {
        high = low;
        maxInput.value = low;
      }
    }

    paint(wrap, low, high);

    var floor = parseFloat(minInput.min);
    var ceiling = parseFloat(minInput.max);
    var fieldMin = document.getElementById(wrap.dataset.swanTargetMin);
    var fieldMax = document.getElementById(wrap.dataset.swanTargetMax);

    if (fieldMin) fieldMin.value = low <= floor ? '' : String(low);
    if (fieldMax) fieldMax.value = high >= ceiling ? '' : String(high);
  }

  function onFieldChange(event) {
    var field = event.target;
    if (!field.id) return;

    var wrap = document.querySelector(
      '[data-swan-target-min="' + field.id + '"], [data-swan-target-max="' + field.id + '"]'
    );
    if (!wrap) return;

    var minInput = wrap.querySelector('[data-swan-range-min]');
    var maxInput = wrap.querySelector('[data-swan-range-max]');
    var floor = parseFloat(minInput.min);
    var ceiling = parseFloat(minInput.max);

    var fieldMin = document.getElementById(wrap.dataset.swanTargetMin);
    var fieldMax = document.getElementById(wrap.dataset.swanTargetMax);

    var low = fieldMin && fieldMin.value ? parseFloat(fieldMin.value.replace(/[^0-9.]/g, '')) : floor;
    var high = fieldMax && fieldMax.value ? parseFloat(fieldMax.value.replace(/[^0-9.]/g, '')) : ceiling;

    if (isNaN(low)) low = floor;
    if (isNaN(high)) high = ceiling;
    if (low > high) low = high;

    minInput.value = low;
    maxInput.value = high;
    paint(wrap, low, high);
  }

  document.addEventListener('input', onRangeInput);
  document.addEventListener('change', onFieldChange);

  /* -------------------------------------------------------- endless scroll */

  function nextLinkIn(scope) {
    return (
      scope.querySelector('.pagination__item--next') ||
      scope.querySelector('.pagination__list li:last-child a[href*="page="]')
    );
  }

  function setupEndlessScroll() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var pagination = container.querySelector('.pagination-wrapper');
    if (!pagination || pagination.dataset.swanEndless) return;

    var next = nextLinkIn(pagination);
    if (!next) return;

    var nav = pagination.querySelector('nav') || pagination.querySelector('.pagination');

    // No IntersectionObserver: leave Dawn's numbered pagination alone
    if (!('IntersectionObserver' in window)) return;

    pagination.dataset.swanEndless = 'ready';
    if (nav) nav.hidden = true;

    var nextUrl = next.getAttribute('href');
    var loading = false;

    var spinner = document.createElement('p');
    spinner.className = 'swan-endless__status';
    spinner.setAttribute('role', 'status');
    spinner.hidden = true;
    spinner.textContent = 'Loading more\u2026';

    var sentinel = document.createElement('div');
    sentinel.className = 'swan-endless__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');

    pagination.appendChild(spinner);
    pagination.appendChild(sentinel);

    function restorePagination() {
      if (nav) nav.hidden = false;
      spinner.remove();
      sentinel.remove();
      observer.disconnect();
    }

    function loadNext() {
      if (loading || !nextUrl) return;
      loading = true;
      spinner.hidden = false;

      fetch(nextUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('bad response');
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var incoming = parsed.getElementById('product-grid');
          var grid = document.getElementById('product-grid');
          if (!incoming || !grid) throw new Error('grid missing');

          while (incoming.firstElementChild) {
            grid.appendChild(incoming.firstElementChild);
          }

          var incomingPagination = parsed.querySelector('.pagination-wrapper');
          var following = incomingPagination ? nextLinkIn(incomingPagination) : null;
          nextUrl = following ? following.getAttribute('href') : null;

          loading = false;
          spinner.hidden = true;

          if (!nextUrl) {
            observer.disconnect();
            spinner.remove();
            sentinel.remove();
          } else {
            // The sentinel may still be on screen after a short page
            observer.unobserve(sentinel);
            observer.observe(sentinel);
          }
        })
        .catch(function () {
          loading = false;
          restorePagination();
        });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) loadNext();
        });
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(sentinel);
  }

  function watchGrid() {
    setupEndlessScroll();

    var container = document.getElementById('ProductGridContainer');
    if (!container || !window.MutationObserver) return;

    // Dawn replaces the container's contents after every filter change
    new MutationObserver(function () {
      setupEndlessScroll();
    }).observe(container, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchGrid);
  } else {
    watchGrid();
  }

  document.addEventListener('shopify:section:load', watchGrid);
})();
